import { useQuery, useMutation, useQueryClient } from "react-query";
import { preventiviApi, Preventivo, PreventivoRow } from "../services/preventiviApi";

export function usePreventivo(id: string | undefined) {
  return useQuery(
    ["preventivo", id],
    () => preventiviApi.get(id!),
    {
      enabled: !!id,
      refetchInterval: (data) => (data?.status === "processing" ? 3000 : false),
      // Keep workspace data fresh for 60 s — navigating back is instant from cache.
      // Mutations (updateRow, close, etc.) call invalidateQueries so edits stay live.
      staleTime: 60_000,
    }
  );
}

export function useUpdateRow(preventivoId: string) {
  const qc = useQueryClient();
  return useMutation(
    ({ rowId, data }: { rowId: string; data: Partial<PreventivoRow> }) =>
      preventiviApi.updateRow(preventivoId, rowId, data),
    {
      onSuccess: () => {
        qc.invalidateQueries(["preventivo", preventivoId]);
      },
    }
  );
}

export function useReorderRows(preventivoId: string) {
  const qc = useQueryClient();
  return useMutation(
    (ordered_row_ids: string[]) => preventiviApi.reorderRows(preventivoId, ordered_row_ids),
    {
      onMutate: async (ordered_row_ids) => {
        await qc.cancelQueries(["preventivo", preventivoId]);
        const prev = qc.getQueryData<Preventivo>(["preventivo", preventivoId]);
        if (prev?.rows) {
          const rowMap = Object.fromEntries(prev.rows.map((r) => [r.id, r]));
          const reordered = ordered_row_ids.map((id, pos) => ({
            ...rowMap[id],
            position: pos,
          }));
          qc.setQueryData(["preventivo", preventivoId], {
            ...prev,
            rows: reordered,
          });
        }
        return { prev };
      },
      onError: (_err, _vars, ctx: any) => {
        if (ctx?.prev) qc.setQueryData(["preventivo", preventivoId], ctx.prev);
      },
    }
  );
}
